/**
 * ErrorBoundary — łapie React runtime errors i pokazuje friendly UI
 * zamiast white screen. Krytyczne na prod gdzie błędy są minified —
 * bez tego user widzi pustą stronę i nic nie wie co się stało.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

const AUTO_RECOVERY_KEY = 'rf-error-boundary-recovery';

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, errorInfo: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('[ErrorBoundary] Caught:', error, errorInfo);
    this.setState({ errorInfo });

    // Auto-recovery: pierwszy crash w sesji → cichy hardReload (czysci stary
    // SW + cache). Jeśli po tym dalej crash → user widzi UI i decyduje sam.
    // sessionStorage flag zapobiega infinite loop'owi (reload kasuje memory
    // ale sessionStorage przeżywa do zamknięcia tabu).
    try {
      const alreadyTried = sessionStorage.getItem(AUTO_RECOVERY_KEY);
      if (!alreadyTried) {
        sessionStorage.setItem(AUTO_RECOVERY_KEY, '1');
        console.warn('[ErrorBoundary] Auto-recovery: cleaning cache + reload (one-time per session)');
        import('../lib/hardReload').then(({ hardReload }) => hardReload());
      }
    } catch { /* sessionStorage może być blocked w private mode */ }
  }

  handleReload = async (): Promise<void> => {
    const { hardReload } = await import('../lib/hardReload');
    hardReload();
  };

  render(): ReactNode {
    if (!this.state.error) return this.props.children;
    const err = this.state.error;
    return (
      <div className="min-h-screen bg-slate-100 dark:bg-slate-900 flex items-center justify-center p-4">
        <div className="max-w-lg w-full bg-white dark:bg-slate-800 rounded-2xl shadow-card-hover border border-rose-200 dark:border-rose-500/30 p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-full bg-rose-100 dark:bg-rose-500/15 flex items-center justify-center text-rose-600 dark:text-rose-400 text-2xl">
              ⚠
            </div>
            <div>
              <h1 className="text-lg font-bold text-slate-900 dark:text-white">Coś poszło nie tak</h1>
              <p className="text-xs text-slate-500 dark:text-slate-400">Apka napotkała błąd przy renderze</p>
            </div>
          </div>

          <div className="mt-4 p-3 rounded-lg bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700 max-h-64 overflow-auto">
            <p className="text-xs font-mono font-bold text-rose-700 dark:text-rose-300 mb-1 break-words">
              {err.name}: {err.message}
            </p>
            {err.stack && (
              <pre className="text-[10px] font-mono text-slate-600 dark:text-slate-400 whitespace-pre-wrap break-words leading-tight">
                {err.stack.split('\n').slice(0, 8).join('\n')}
              </pre>
            )}
          </div>

          <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
            <strong>Pomocne info dla diagnostyki:</strong> sprawdź czy AdBlock / Brave Shields nie blokują <code className="px-1 py-0.5 bg-slate-100 dark:bg-slate-700 rounded">firestore.googleapis.com</code> (najczęstsza przyczyna).
          </p>

          <div className="mt-4 flex gap-2">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 text-sm font-semibold hover:bg-slate-50 dark:hover:bg-slate-700/40 transition"
            >
              Odśwież
            </button>
            <button
              type="button"
              onClick={this.handleReload}
              className="flex-1 px-4 py-2.5 rounded-xl bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold shadow-card transition"
            >
              Wymuś świeżą wersję
            </button>
          </div>
        </div>
      </div>
    );
  }
}
